// @greenlight-spec: 20-agent-plugins.md#marketplace-repo-bootstrap
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';

import { MarketplaceEditor } from '@/components/greenlight/marketplace/MarketplaceEditor';
import { AgentSetup } from '@/components/settings/AgentSetup';
import type { BootstrapMarketplace } from '@/components/greenlight/marketplace/useMarketplaceEditor';
import { StepHeader } from '@/components/install/StepHeader';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { mergeRepository } from '@/lib/marketplace';
import {
  PluginMarketplaceStatusSchema,
  type InstallationState,
  type PluginMarketplaceBootstrapResult,
  type PluginMarketplaceStatus,
} from '@greenlight/shared';
import type { InstallScmInstallation } from '@greenlight/contract';

interface PluginMarketplaceStepProps {
  installationState: InstallationState;
  /** Live SCM connection — supplies the org prefix on the create form. */
  scmInstallation: InstallScmInstallation | null;
  /** Control-plane base URL from the install context — the MCP URL in the rollout steps derives from it. */
  greenlightBaseUrl: string;
  orgSlug: string;
  blocked: boolean;
  onAdvance: () => void;
  isSubmitting: boolean;
}

function persistedRepositories(installationState: InstallationState): PluginMarketplaceStatus[] {
  const details = installationState.steps?.plugin_marketplace?.details;
  const parsed = PluginMarketplaceStatusSchema.array().safeParse(details?.['repositories']);
  return parsed.success ? parsed.data : [];
}

/** Installs completed before multi-repo support persisted `{ repo_full_name }` only. */
function legacyRepoFullName(installationState: InstallationState): string | null {
  const details = installationState.steps?.plugin_marketplace?.details;
  const value = details?.['repo_full_name'];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function PluginMarketplaceStep({
  installationState,
  scmInstallation,
  greenlightBaseUrl,
  orgSlug,
  blocked,
  onAdvance,
  isSubmitting,
}: PluginMarketplaceStepProps): React.JSX.Element {
  const persisted = persistedRepositories(installationState);
  const [repositories, setRepositories] = React.useState(persisted);
  const bootstrapMutation = api.install.bootstrapPluginMarketplace.useMutation();
  const stepComplete = installationState.steps?.plugin_marketplace?.status === 'complete';
  const legacyRepo = repositories.length === 0 ? legacyRepoFullName(installationState) : null;
  const connected = scmInstallation !== null && !scmInstallation.suspended;
  const dnsSetup = api.install.getDnsSetup.useQuery(['install-dns-setup'], {});
  const privateInstall =
    dnsSetup.data?.status === 200 && dnsSetup.data.body.access_profile === 'private';
  // The same Agent setup as Settings, fed from the wizard's own data; the installer is IT.
  const agentSetup = {
    marketplace_repositories: repositories,
    sparse_path: null,
    mcp_url: `${greenlightBaseUrl.replace(/\/+$/, '')}/mcp`,
    org_slug: orgSlug,
    private_install: privateInstall,
    org_admins: [],
  };
  // Once a repository exists, moving on is the primary action; creating another is secondary.
  const continueOffered = repositories.length > 0 || stepComplete;

  React.useEffect(() => setRepositories(persisted), [installationState]);

  const handleBootstrap: BootstrapMarketplace = async (
    body,
  ): Promise<PluginMarketplaceBootstrapResult> => {
    const result = await bootstrapMutation.mutateAsync({ body });
    if (result.status !== 200) throw result;
    setRepositories((current) => mergeRepository(current, result.body.marketplace));
    return result.body;
  };

  return (
    <div className="space-y-6" data-testid="step-plugin_marketplace">
      <StepHeader step="plugin_marketplace" title="Agent plugin">
        Your coding agents reach Greenlight with a plugin that&rsquo;s published in a GitHub
        repository in your organization. Create that repository here.
      </StepHeader>

      {blocked ? (
        <div
          className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
          data-testid="step-blocked"
        >
          This step is blocked until the GitHub App step is complete.
        </div>
      ) : (
        <>
          {legacyRepo && (
            <div
              className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground"
              data-testid="marketplace-legacy-repo"
            >
              <span className="font-mono text-foreground">{legacyRepo}</span> already exists for
              this org. Continue, or add another repository.
            </div>
          )}
          <MarketplaceEditor
            repositories={repositories}
            githubAppReady={connected}
            orgHandle={connected ? scmInstallation.orgHandle : null}
            recoveryContext="wizard"
            createVariant={continueOffered ? 'outline' : 'default'}
            onBootstrap={handleBootstrap}
          />
          {repositories.length > 0 && (
            <section className="space-y-3 border-t pt-6" data-testid="wizard-agent-setup">
              <h2 className="text-base font-semibold tracking-tight">
                Roll it out to your coding agents
              </h2>
              <AgentSetup agentSetup={agentSetup} userRole="org_admin" showHeading={false} />
            </section>
          )}
          {continueOffered && (
            <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Add another repository now, or continue and manage them later in Settings.
              </p>
              <Button
                type="button"
                className="h-11 w-full md:h-9 sm:w-auto sm:shrink-0"
                onClick={onAdvance}
                disabled={isSubmitting || bootstrapMutation.isPending}
                data-testid="step-next"
              >
                {isSubmitting ? 'Saving…' : 'Continue'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
